import React, { useState } from 'react';
import { ExternalLink, Play } from 'lucide-react';
import TwitchPlayer from './TwitchPlayer';
import { normalizeTwitchThumbnailUrl } from './leagueHome/leagueHomeUtils';
import type { LscNeedsAttentionPlayback } from '../types';

type NeedsAttentionMediaPreviewProps = {
  playback?: LscNeedsAttentionPlayback | null;
  matchupLabel: string;
};

/**
 * Commissioner-safe compact review media. A live review item renders an
 * embedded Twitch channel player (the stream is currently live, so there is no
 * VOD id yet); a VOD review item renders a thumbnail that expands into an
 * embedded Twitch VOD player on demand. Never renders technical identifiers.
 */
const NeedsAttentionMediaPreview: React.FC<NeedsAttentionMediaPreviewProps> = ({
  playback,
  matchupLabel,
}) => {
  const [showPlayer, setShowPlayer] = useState(false);

  if (!playback) return null;

  const isTwitch = (playback.provider ?? 'twitch') === 'twitch';
  const videoId = isTwitch ? playback.twitch_video_id ?? undefined : undefined;
  const channelLogin = isTwitch ? playback.channel_login ?? undefined : undefined;
  const thumbnailUrl = normalizeTwitchThumbnailUrl(playback.thumbnail_url) ?? undefined;
  const watchUrl = playback.watch_url ?? undefined;
  const hasVodEmbed = Boolean(videoId);
  const isLive = Boolean(playback.is_live && channelLogin);
  const title = playback.title ?? matchupLabel;

  // Live review item: embed the live channel directly. The stream is live, so
  // there is no VOD id to expand into — surface it immediately with a fallback
  // link to open the live stream on Twitch if the embed cannot render.
  if (isLive) {
    return (
      <div className="max-w-xl" data-testid="needs-attention-live-player">
        <TwitchPlayer
          mode="live"
          channelLogin={channelLogin}
          title={title}
          thumbnailUrl={thumbnailUrl}
          fallbackUrl={watchUrl}
          showExternalLink={false}
        />
        {watchUrl ? (
          <a
            href={watchUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-2 text-xs text-primary-300 hover:text-primary-200"
            data-testid="needs-attention-live-watch-link"
          >
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            Open live stream on Twitch
          </a>
        ) : null}
      </div>
    );
  }

  if (showPlayer && hasVodEmbed) {
    return (
      <div className="max-w-xl" data-testid="needs-attention-player">
        <TwitchPlayer
          mode="vod"
          videoId={videoId}
          title={title}
          thumbnailUrl={thumbnailUrl}
          fallbackUrl={watchUrl}
        />
      </div>
    );
  }

  return (
    <div className="max-w-xl" data-testid="needs-attention-media">
      <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-secondary-700 bg-black">
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt=""
            className="h-full w-full object-cover"
            data-testid="needs-attention-thumbnail"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-secondary-500">
            Preview unavailable
          </div>
        )}
        {hasVodEmbed ? (
          <button
            type="button"
            onClick={() => setShowPlayer(true)}
            className="absolute inset-0 flex items-center justify-center bg-black/30 transition hover:bg-black/50"
            aria-label="Play VOD preview"
            data-testid="needs-attention-play"
          >
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary-500/90 text-white">
              <Play className="h-6 w-6" aria-hidden />
            </span>
          </button>
        ) : null}
      </div>
      {watchUrl ? (
        <a
          href={watchUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-flex items-center gap-2 text-xs text-primary-300 hover:text-primary-200"
          data-testid="needs-attention-watch-link"
        >
          <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          Open on Twitch
        </a>
      ) : null}
    </div>
  );
};

export default NeedsAttentionMediaPreview;
